import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Check,
  AlertCircle,
  ExternalLink,
  MessageSquare,
  X,
  Upload,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Clock,
  CheckCircle2,
  XCircle,
  Diamond,
  Pencil,
  StickyNote,
} from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AdsDateRangePicker } from "@/components/common/AdsDateRangePicker";
import type { DateRange } from "@/components/common/DateRangeFilter";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { modCanWorkspace, type ModeratorPermissions } from "@/lib/moderator-permissions";

export const Route = createFileRoute("/_app/creatives")({
  component: CreativesPage,
});

type Dept = "editor" | "graphic_designer" | "ai";
type SubTable = "editor_video_submissions" | "designer_submissions" | "ai_submissions";
type StatusTab = "pending" | "correction" | "changes" | "approved" | "rejected";

interface Row {
  id: string;
  table: SubTable;
  user_id: string;
  user_name: string | null;
  date: string;
  creative_id?: string;
  product_name?: string;
  brand?: string;
  video_type?: string;
  creative_description?: string;
  category?: string;
  type?: string;
  drive_link: string;
  review_status: "pending" | "approved" | "changes_requested" | "rejected";
  correction_status: string;
  correction_note: string | null;
  correction_count: number;
  points: number;
  reviewer_name: string | null;
}

function CreativesPage() {
  const { profile, role } = useAuth();
  const qc = useQueryClient();
  const isAdmin = role === "admin";
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const canReview = isAdmin || (role === "moderator" && modCanWorkspace(perms, "creatives_review"));

  const [dept, setDept] = useState<Dept>("editor");
  const [statusTab, setStatusTab] = useState<StatusTab>("pending");
  const [submittedBy, setSubmittedBy] = useState<string>("all");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("latest");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<DateRange>({ from: "", to: "" });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [actionRow, setActionRow] = useState<Row | null>(null);
  const [actionType, setActionType] = useState<"changes" | "correction" | "reject">("changes");
  const [note, setNote] = useState("");
  const [reopenRow, setReopenRow] = useState<Row | null>(null);

  const { data: rawRows = [], isLoading } = useQuery({
    queryKey: ["creatives", dept],
    enabled: canReview,
    queryFn: async () => {
      if (dept === "editor") {
        const { data, error } = await supabase
          .from("editor_video_submissions")
          .select("id, user_id, submission_date, creative_id, product_name, brand, video_type, creative_drive_link, review_status, correction_status, correction_note, correction_count, points, reviewed_by")
          .is("deleted_at", null)
          .order("submission_date", { ascending: false })
          .limit(500);
        if (error) throw error;
        return data ?? [];
      } else {
        const { data, error } = await supabase
          .from(dept === "ai" ? "ai_submissions" : "designer_submissions")
          .select("id, user_id, submission_date, creative_description, category, type, drive_link, review_status, correction_status, correction_note, correction_count, points, reviewed_by")
          .is("deleted_at", null)
          .order("submission_date", { ascending: false })
          .limit(500);
        if (error) throw error;
        return data ?? [];
      }
    },
  });

  const userIds = useMemo(() => {
    const s = new Set<string>();
    rawRows.forEach((r: any) => { s.add(r.user_id); if (r.reviewed_by) s.add(r.reviewed_by); });
    return Array.from(s);
  }, [rawRows]);

  const { data: profilesMap = {} } = useQuery({
    queryKey: ["creatives-profiles", userIds.join(",")],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles_directory")
        .select("id, full_name")
        .in("id", userIds);
      if (error) throw error;
      const map: Record<string, { full_name: string }> = {};
      (data ?? []).forEach((p) => { if (p.id && p.full_name) map[p.id] = { full_name: p.full_name }; });
      return map;
    },
  });

  const allRows: Row[] = useMemo(() => {
    return rawRows.map((r: any): Row => ({
      id: r.id,
      table: dept === "editor" ? "editor_video_submissions" : dept === "ai" ? "ai_submissions" : "designer_submissions",
      user_id: r.user_id,
      user_name: profilesMap[r.user_id]?.full_name ?? null,
      date: r.submission_date,
      creative_id: r.creative_id,
      product_name: r.product_name,
      brand: r.brand,
      video_type: r.video_type,
      creative_description: r.creative_description,
      category: r.category,
      type: r.type,
      drive_link: r.creative_drive_link ?? r.drive_link,
      review_status: r.review_status,
      correction_status: r.correction_status,
      correction_note: r.correction_note,
      correction_count: r.correction_count ?? 0,
      points: r.points ?? 0,
      reviewer_name: r.reviewed_by ? profilesMap[r.reviewed_by]?.full_name ?? null : null,
    }));
  }, [rawRows, profilesMap, dept]);

  // Counts for tabs (independent of statusTab, but respect other filters)
  const statusBuckets = useMemo(() => {
    const base = allRows.filter((r) => {
      if (submittedBy !== "all" && r.user_id !== submittedBy) return false;
      if (productFilter !== "all" && (r.product_name ?? r.category ?? "") !== productFilter) return false;
      if (typeFilter !== "all" && (r.video_type ?? r.type ?? "") !== typeFilter) return false;
      if (range.from && r.date < range.from) return false;
      if (range.to && r.date > range.to) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [r.creative_id, r.product_name, r.brand, r.creative_description, r.category, r.user_name].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return {
      pending: base.filter((r) => r.review_status === "pending" && r.correction_status !== "fix"),
      correction: base.filter((r) => r.correction_status === "fix"),
      changes: base.filter((r) => r.review_status === "changes_requested" && r.correction_status !== "fix"),
      approved: base.filter((r) => r.review_status === "approved" && r.correction_status !== "fix"),
      rejected: base.filter((r) => r.review_status === "rejected"),
    };
  }, [allRows, submittedBy, productFilter, typeFilter, range, search]);

  const filtered = useMemo(() => {
    const rows = statusBuckets[statusTab];
    return [...rows].sort((a, b) => {
      if (sortBy === "oldest") return a.date.localeCompare(b.date);
      return b.date.localeCompare(a.date);
    });
  }, [statusBuckets, statusTab, sortBy]);

  // Group filtered by date
  const grouped = useMemo(() => {
    const map = new Map<string, Row[]>();
    filtered.forEach((r) => {
      const k = r.date;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const submitterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    allRows.forEach((r) => { if (r.user_name) seen.set(r.user_id, r.user_name); });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allRows]);

  const productOptions = useMemo(() => {
    const s = new Set<string>();
    allRows.forEach((r) => {
      const v = r.product_name ?? r.category;
      if (v) s.add(v);
    });
    return Array.from(s).sort();
  }, [allRows]);

  const typeOptions = useMemo(() => {
    const s = new Set<string>();
    allRows.forEach((r) => {
      const v = r.video_type ?? r.type;
      if (v) s.add(v);
    });
    return Array.from(s).sort();
  }, [allRows]);

  const reviewMut = useMutation({
    mutationFn: async ({ row, action, note }: { row: Row; action: "approve" | "changes" | "correction" | "reject" | "reopen"; note?: string }) => {
      const { error } = await supabase.rpc("review_creative", {
        _table: row.table,
        _id: row.id,
        _action: action,
        _note: note ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      toast.success(
        vars.action === "approve" ? "Approved" :
        vars.action === "changes" ? "Changes requested" :
        vars.action === "correction" ? "Marked for correction" :
        vars.action === "reject" ? "Rejected" : "Re-opened",
      );
      qc.invalidateQueries({ queryKey: ["creatives"] });
      setActionRow(null);
      setNote("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openModal = (row: Row, type: "changes" | "correction" | "reject") => {
    setActionRow(row);
    setActionType(type);
    setNote("");
  };

  const submitNote = () => {
    if (!actionRow) return;
    if (actionType === "changes" && note.trim().length < 10) {
      toast.error("Note must be at least 10 characters");
      return;
    }
    if (actionType !== "changes" && note.trim().length < 30) {
      toast.error("Note must be at least 30 characters");
      return;
    }
    reviewMut.mutate({ row: actionRow, action: actionType, note: note.trim() });
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!canReview) {
    return (
      <>
        <AppHeader subtitle="Creatives Review" />
        <div className="p-6">
          <Card><CardContent className="p-6 text-sm text-muted-foreground">You don't have permission to review creatives.</CardContent></Card>
        </div>
      </>
    );
  }

  const formatDateLabel = (iso: string) => {
    try {
      const d = parseISO(iso);
      if (isToday(d)) return `Today • ${format(d, "MMM d")}`;
      if (isYesterday(d)) return `Yesterday • ${format(d, "MMM d")}`;
      return format(d, "MMM d");
    } catch {
      return iso;
    }
  };

  const tabs: { key: StatusTab; label: string; icon: typeof Clock; iconColor: string; count: number }[] = [
    { key: "pending", label: "Pending Review", icon: Clock, iconColor: "text-primary", count: statusBuckets.pending.length },
    { key: "correction", label: "In Correction", icon: AlertCircle, iconColor: "text-amber-500", count: statusBuckets.correction.length },
    { key: "changes", label: "Changes", icon: StickyNote, iconColor: "text-blue-600", count: statusBuckets.changes.length },
    { key: "approved", label: "Approved", icon: CheckCircle2, iconColor: "text-green-600", count: statusBuckets.approved.length },
    { key: "rejected", label: "Rejected", icon: XCircle, iconColor: "text-red-600", count: statusBuckets.rejected.length },
  ];

  const renderActions = (r: Row) => {
    const isRejected = r.review_status === "rejected";
    const isApproved = r.review_status === "approved" && r.correction_status !== "fix";

    return (
      <div className="flex items-center justify-end gap-1.5">
        {!isRejected && !isApproved && (
          <>
            <button
              type="button"
              title="Approve"
              onClick={() => reviewMut.mutate({ row: r, action: "approve" })}
              disabled={reviewMut.isPending}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Request correction"
              onClick={() => openModal(r, "correction")}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Request changes (note, no points deducted)"
              onClick={() => openModal(r, "changes")}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
            >
              <StickyNote className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Reject"
              onClick={() => openModal(r, "reject")}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        )}
        {isApproved && (
          <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 border border-green-200">
            <Check className="h-3 w-3" /> Approved
          </span>
        )}
        {isRejected && (
          <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 border border-red-200">
            <X className="h-3 w-3" /> Rejected
          </span>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem disabled className="text-xs flex justify-between">
              <span>Points</span>
              <span className="font-semibold">{r.points}</span>
            </DropdownMenuItem>
            <DropdownMenuItem disabled className="text-xs flex justify-between">
              <span>Corrections</span>
              <span className="font-semibold">{r.correction_count}</span>
            </DropdownMenuItem>
            {r.reviewer_name && (
              <DropdownMenuItem disabled className="text-xs flex justify-between">
                <span>Reviewer</span>
                <span className="font-medium truncate ml-2">{r.reviewer_name}</span>
              </DropdownMenuItem>
            )}
            {canReview && (isRejected || isApproved) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setReopenRow(r)}>
                  Re-open
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  return (
    <>
      <AppHeader subtitle="Review Editor & Designer submissions" />
      <div className="p-6 space-y-5">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Creatives</h1>
            <p className="text-sm text-muted-foreground mt-1">Review and manage editor submissions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2">
              <Upload className="h-4 w-4" /> Export
            </Button>
            <Button className="gap-2 bg-[#7c3aed] hover:bg-[#6d28d9] text-white">
              <Plus className="h-4 w-4" /> Bulk Actions
            </Button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="border-b border-border">
          <div className="flex items-center gap-1 -mb-px overflow-x-auto">
            {tabs.map((t) => {
              const active = statusTab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setStatusTab(t.key)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                    active
                      ? "border-[#7c3aed] text-[#7c3aed]"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className={cn("h-4 w-4", active ? "text-[#7c3aed]" : t.iconColor)} />
                  <span>{t.label}</span>
                  <span
                    className={cn(
                      "inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full px-1.5 text-[11px] font-semibold",
                      active ? "bg-[#7c3aed]/10 text-[#7c3aed]" : "bg-muted text-muted-foreground",
                    )}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, editor, product..."
              className="pl-9 h-9 bg-white"
            />
          </div>
          <AdsDateRangePicker value={range} onChange={setRange} />
          <Select value={dept === "editor" ? submittedBy : submittedBy} onValueChange={setSubmittedBy}>
            <SelectTrigger className="h-9 w-auto min-w-[140px] bg-white">
              <SelectValue placeholder="All Editors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Editors</SelectItem>
              {submitterOptions.map(([id, name]) => (
                <SelectItem key={id} value={id}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger className="h-9 w-auto min-w-[140px] bg-white">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {productOptions.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-9 w-auto min-w-[120px] bg-white">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {typeOptions.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-9 w-auto min-w-[130px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">Sort: Latest</SelectItem>
              <SelectItem value="oldest">Sort: Oldest</SelectItem>
            </SelectContent>
          </Select>
          <Select value={dept} onValueChange={(v) => setDept(v as Dept)}>
            <SelectTrigger className="h-9 w-auto min-w-[140px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">Editors</SelectItem>
              <SelectItem value="graphic_designer">Graphic Designer</SelectItem>
              <SelectItem value="ai">AI</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-9 gap-2 bg-white">
            <Filter className="h-4 w-4" /> Filters
          </Button>
        </div>

        {/* Grouped list */}
        <div className="space-y-6">
          {isLoading ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading...</CardContent></Card>
          ) : grouped.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No submissions match these filters</CardContent></Card>
          ) : grouped.map(([date, rows]) => (
            <div key={date} className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <Diamond className="h-4 w-4 text-[#7c3aed] fill-[#7c3aed]/20" />
                <span className="text-sm font-semibold">{formatDateLabel(date)}</span>
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                        <th className="px-4 py-3 w-10"></th>
                        <th className="px-4 py-3 font-medium">Creative</th>
                        <th className="px-4 py-3 font-medium">Editor</th>
                        <th className="px-4 py-3 font-medium">Product</th>
                        <th className="px-4 py-3 font-medium">Type</th>
                        <th className="px-4 py-3 font-medium">Format</th>
                        <th className="px-4 py-3 font-medium">Preview</th>
                        <th className="px-4 py-3 font-medium">Remarks</th>
                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const product = r.product_name ?? r.category ?? "—";
                        const cid = r.creative_id ?? r.id.slice(0, 8).toUpperCase();
                        const type = r.video_type ?? r.type ?? "—";
                        const fmt = r.brand ?? "—";
                        return (
                          <tr key={`${r.table}:${r.id}`} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-4">
                              <Checkbox
                                checked={selected.has(r.id)}
                                onCheckedChange={() => toggleSelect(r.id)}
                              />
                            </td>
                            <td className="px-4 py-4">
                              <div className="font-semibold text-foreground">{cid}</div>
                              <div className="text-xs text-muted-foreground truncate max-w-[180px]">{product}</div>
                            </td>
                            <td className="px-4 py-4 text-foreground">{r.user_name ?? "—"}</td>
                            <td className="px-4 py-4 text-foreground truncate max-w-[180px]">{product}</td>
                            <td className="px-4 py-4 text-foreground">{type}</td>
                            <td className="px-4 py-4 text-foreground">{fmt}</td>
                            <td className="px-4 py-4">
                              {r.drive_link ? (
                                <a
                                  href={r.drive_link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[#7c3aed] hover:underline"
                                >
                                  Open <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-4 max-w-[160px]">
                              {r.correction_note ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 text-muted-foreground cursor-help">
                                        <MessageSquare className="h-3 w-3" />
                                        <span className="truncate text-xs">Note</span>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">{r.correction_note}</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-4 py-4">{renderActions(r)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!actionRow} onOpenChange={(o) => !o && setActionRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "changes" ? "Request Changes" : actionType === "correction" ? "Mark Correction" : "Reject Submission"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {actionType === "correction"
                ? "Correction will deduct 2 points (permanent). Note required."
                : actionType === "reject"
                ? "Rejection sets points to zero and excludes this from monthly totals. Note required."
                : actionType === "changes"
                ? "A note for the editor. No points deducted. Minimum 10 characters required."
                : "Submitter will be notified to revise. Note required."}
            </p>
            {actionType === "changes" ? (
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 100))}
                placeholder="Note (minimum 10 characters)"
                rows={3}
              />
            ) : (
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={actionType === "reject" ? "Reason for rejection (min 30 characters)" : "Explain what needs to change (min 30 characters)"}
                rows={5}
              />
            )}
            <p className="text-[10px] text-muted-foreground">
              {actionType === "changes"
                ? `${note.trim().length}/10 minimum (max 100)`
                : `${note.trim().length} / 30 minimum`}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionRow(null)}>Cancel</Button>
            <Button
              onClick={submitNote}
              disabled={
                reviewMut.isPending ||
                (actionType === "changes"
                  ? note.trim().length < 10
                  : note.trim().length < 30)
              }
              variant={actionType === "reject" ? "destructive" : "default"}
            >
              {reviewMut.isPending ? "Saving..." : actionType === "reject" ? "Reject →" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reopenRow} onOpenChange={(o) => !o && setReopenRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-open this creative?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            It will go back to Pending and become editable by the submitter again.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenRow(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (reopenRow) {
                  reviewMut.mutate(
                    { row: reopenRow, action: "reopen" },
                    { onSuccess: () => setReopenRow(null) },
                  );
                }
              }}
              disabled={reviewMut.isPending}
            >
              {reviewMut.isPending ? "Re-opening..." : "Re-open"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
